import { useSelector } from "react-redux";
import { Controller } from "react-hook-form";

import Icon from "./Icon";
import InfoItem from "../helpers/InfoItem/InfoItem";
import Badge from "./Badge";
import ErrorMessage from "./ErrorMessage";
import DateTimePicker from "./DateTimePicker";
import useUploadFile from "../hook/useUploadFile";
import { convertDate, numberSeparate, removeNumberSeparate } from "../utils";
import { fieldTypeItems } from "../utils/enum";
import { hostname } from "../services/configServices/config";

const FieldItem = ({ item, watch, control, isSubmitted }) => {
  const loader = useSelector((state) => state.loader);

  const { handleUploadFile } = useUploadFile();

  const input = (value, onChange, onBlur) => {
    let result = "";
    const placeholder = `${item.label} را وارد نمایید`;
    switch (item.type) {
      case fieldTypeItems[0]:
        result = (
          <input
            id={item._id}
            name={item.name}
            type="text"
            placeholder={placeholder}
            className="form-control radius-8"
            onChange={(e) => onChange(e.target.value)}
            onBlur={onBlur}
            value={value}
            inputMode="text"
          />
        );
        break;
      case fieldTypeItems[1]:
        result = (
          <input
            id={item._id}
            name={item.name}
            type="text"
            placeholder={placeholder}
            className="form-control radius-8"
            onChange={(e) => {
              const value = removeNumberSeparate(e.target.value);
              onChange(value);
            }}
            onBlur={onBlur}
            value={numberSeparate(value)}
            inputMode="decimal"
          />
        );
        break;
      case fieldTypeItems[2]:
        result = (
          <div className="form-check style-check py-12 px-16 border radius-8 position-relative">
            <label
              htmlFor={item._id}
              className="position-absolute w-100 h-100 start-0 top-0"
            />
            <div className="d-flex align-items-center gap-3 justify-content-between">
              <span className="form-check-label line-height-1 fw-medium text-secondary-light">
                {item.label}
              </span>
              <input
                className="form-check-input border border-neutral-300"
                type="checkbox"
                id={item._id}
                name={item.name}
                onChange={(e) => {
                  const value = e.target.checked;
                  onChange(value);
                }}
                onBlur={onBlur}
                checked={value}
              />
            </div>
          </div>
        );
        break;
      case fieldTypeItems[3]:
        result = (
          <div className="form-switch switch-primary py-12 px-16 border radius-8 position-relative">
            <label
              htmlFor={item._id}
              className="position-absolute w-100 h-100 start-0 top-0"
            />
            <div className="d-flex align-items-center gap-3 justify-content-between">
              <span className="form-check-label line-height-1 fw-medium text-secondary-light">
                {item.label}
              </span>
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id={item._id}
                name={item.name}
                onChange={(e) => onChange(e.target.checked)}
                onBlur={onBlur}
                checked={value}
              />
            </div>
          </div>
        );
        break;
      case fieldTypeItems[4]:
        result = (
          <DateTimePicker
            id={item._id}
            name={item.name}
            className="form-control radius-8"
            placeholder={placeholder}
            onChange={onChange}
            onBlur={onBlur}
            initValue={value}
          />
        );
        break;
      case fieldTypeItems[5]:
        result = (
          <input
            id={item._id}
            name={item.name}
            type="time"
            placeholder={placeholder}
            className="form-control radius-8"
            onChange={(e) => onChange(e.target.value)}
            onBlur={onBlur}
            value={value}
          />
        );
        break;
      case fieldTypeItems[6]:
        result = (
          <select
            className="form-control radius-8"
            onChange={(e) => onChange(e.target.value)}
            onBlur={onBlur}
            value={value}
            id={item._id}
            name={item.name}
          >
            <option value="" selected hidden>
              {placeholder}
            </option>
            {item.options?.map((option) => (
              <option key={option} value={option} selected={option === value}>
                {option}
              </option>
            ))}
          </select>
        );
        break;
      case fieldTypeItems[7]:
        result = (
          <div className="d-flex align-items-center flex-wrap gap-2">
            {item.options?.map((option, index) => {
              const id = item._id + option + index;
              return (
                <label
                  htmlFor={id}
                  className="bg-neutral-50 px-20 py-12 radius-8 w-100"
                >
                  <span className="form-check checked-light d-flex align-items-center gap-2">
                    <input
                      className="form-check-input"
                      type="radio"
                      name={item.name}
                      id={id}
                      checked={option === value}
                      onChange={() => onChange(option)}
                      onBlur={onBlur}
                    />
                    <span className="form-check-label line-height-1 fw-medium text-secondary-light text-line-1">
                      {option}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        );
        break;
      case fieldTypeItems[8]:
        result = (
          <textarea
            className="form-control radius-8"
            rows={5}
            placeholder={placeholder}
            id={item._id}
            name={item.name}
            onChange={(e) => onChange(e.target.value)}
            onBlur={onBlur}
            value={value}
          />
        );
        break;
      case fieldTypeItems[9]:
        result = (
          <input
            disabled={loader}
            type="file"
            className="form-control radius-8"
            placeholder={placeholder}
            id={item._id}
            name={item.name}
            accept=".jpg,.jpeg,.png,.gif,.docx,.xlsx,.csv,.pdf,.mp4,.avi;capture=camera"
            onChange={async (e) => {
              const file = e.target.files[0];
              handleUploadFile(file, (e) => onChange(hostname + e), 3);
            }}
          />
        );
        break;

      default:
        result = null;
        break;
    }

    return result;
  };

  return (
    <div
      className={`col-12 ${
        [fieldTypeItems[7], fieldTypeItems[8]].includes(item.type)
          ? ""
          : "col-md-6"
      }`}
    >
      <div className="mb-20">
        <div>
          <label
            htmlFor={item._id}
            className="form-label fw-semibold text-primary-light text-sm mb-8"
          >
            {item.label}{" "}
            {item.isRequired && <Badge color="danger">اجباری</Badge>}
          </label>
          <Controller
            control={control}
            render={({ field: { value, onChange, onBlur } }) =>
              input(value, onChange, onBlur)
            }
            name={item.name}
          />
        </div>

        {isSubmitted && item.isRequired && (
          <ErrorMessage
            message={!watch(item.name) && `${item.label} الزامی است`}
          />
        )}
      </div>
    </div>
  );
};

export default FieldItem;

export const FieldDetails = ({ item, details = {} }) => {
  const value = () => {
    let val = details[item.name];

    let result = "";
    switch (item.type) {
      case fieldTypeItems[0]:
        result = val;
        break;
      case fieldTypeItems[1]:
        result = numberSeparate(val);
        break;
      case fieldTypeItems[2]:
        result = (
          <Icon
            icon={
              val ? "solar:shield-check-broken" : "solar:shield-cross-broken"
            }
            className={`text-xxl text-${val ? "success" : "danger"}`}
          />
        );
        break;
      case fieldTypeItems[3]:
        result = (
          <Icon
            icon={
              val ? "solar:shield-check-broken" : "solar:shield-cross-broken"
            }
            className={`text-xxl text-${val ? "success" : "danger"}`}
          />
        );
        break;
      case fieldTypeItems[4]:
        result = convertDate(val);
        break;
      case fieldTypeItems[5]:
        result = val;
        break;
      case fieldTypeItems[6]:
        result = val;
        break;
      case fieldTypeItems[7]:
        result = val;
        break;
      case fieldTypeItems[8]:
        result = val;
        break;
      case fieldTypeItems[9]:
        result = (
          <a
            className="btn-sm px-20 py-0 border border-primary-200 bg-primary-50 bg-hover-primary-100 radius-6"
            href={val}
            target="_blank"
            rel="noreferrer"
          >
            مشاهده
          </a>
        );
        break;

      default:
        result = val;
        break;
    }
    return result;
  };
  return <InfoItem title={item?.label}>{value()}</InfoItem>;
};
